<script lang="ts" setup>

import type { NavigationMenuItem } from '@nuxt/ui'

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

// Device
const deviceStore = useDeviceStore()
const isMobile = deviceStore.isMobile

// Idioma
const { locale, setLocaleCookie, t } = useI18n()

const selectedIdioma = computed(() => locale.value)
setLocaleCookie(locale.value)

// Datos Estáticos del Header (Estructura en código, textos en i18n)
const suggestedDestinations: Ref<StrapiDestination[]> = useState('suggested-destinations', () => [])

// Cargar destinos sugeridos (inicial + al cambiar idioma)
const loadSuggestedDestinations = async () => {
  suggestedDestinations.value = await getStrapiData('suggested-destinations', {
    locale: locale.value
  }) as StrapiDestination[]
  if (suggestedDestinations.value?.length) {
    suggestedDestinations.value.sort((a: StrapiDestination, b: StrapiDestination) => (a.order ?? 0) - (b.order ?? 0))
  }
}
watch(locale, loadSuggestedDestinations)
onMounted(() => loadSuggestedDestinations())

const buttonDestinos = computed(() => ({
  label: t('strapi.global.header.buttonDestinos.label'),
  link: 'destinations',
  url: false
}))

const buttonFunciona = computed(() => ({
  label: t('strapi.global.header.buttonFunciona.label'),
  link: 'como-funciona',
  url: false
}))

const buttonPartner = computed(() => [
  { label: t('strapi.global.header.buttonPartner.agencias'), link: 'https://agencies.byhours.com/login', url: true },
  { label: t('strapi.global.header.buttonPartner.hoteleros'), link: `https://hoteliers.byhours.com/${locale.value}`, url: true },
])

const buttonAyuda = computed(() => [
  { label: t('strapi.global.header.buttonAyuda.contacto'), link: `https://wa.me/34629380206`, url: true },
  { label: t('strapi.global.header.buttonAyuda.preguntas'), link: `https://byhours-support.gorgias.help/${locale.value}-${countryLangCodes[locale.value]}`, url: true }
])

// Helper para construir URLs
const getButtonUrl = (button: { link: string, url?: boolean }) => {
  if (button.url) {
    return button.link
  }
  return `/${locale.value}/${button.link}`
}

// Monedas (estado global desde app.vue)
const currencies: Ref<Currency[]> = useState('currencies', () => [])

// Menu
const open = ref(false)
const openFunciona = ref(false)
const active = ref()

// Cerrar modal al seleccionar idioma o moneda
watch([locale, currency], () => {
  if (open.value) open.value = false
})

const items = computed<NavigationMenuItem[]>(() => [
  {
    label: t('strapi.global.header.labelDestinos'),
    icon: 'i-lucide-map',
    slot: 'destinos',
    children: suggestedDestinations.value.slice(0, 8).map((destino: StrapiDestination) => ({
      label: destino.name,
      to: `/${locale.value}/${destino.slug}-${destino.identifier}d.html`,
      onSelect: () => {
        open.value = false
      }
    })),
  },
  ...(isMobile ? [{
    label: t('buttons.mapa'),
    icon: 'i-lucide-globe',
    to: getButtonUrl(buttonDestinos.value),
    onSelect: () => {
      open.value = false
    }
  }] : []),
  {
    label: buttonFunciona.value.label,
    icon: 'i-lucide-chart-pie',
    to: `/${locale.value}/como-funciona.html`,
    open: openFunciona.value,
    onSelect: () => {
      open.value = false
    }
  },
  {
    label: t('strapi.global.header.labelPartners'),
    icon: 'i-lucide-message-circle',
    slot: 'partners',
    children: buttonPartner.value.map((opcion) => ({
      label: opcion.label,
      to: opcion.link,
      target: '_blank',
      onSelect: () => {
        open.value = false
      }
    }))
  },
  {
    label: t('strapi.global.header.labelAyuda'),
    icon: 'i-lucide-help-circle',
    slot: 'ayuda',
    children: buttonAyuda.value.map((opcion) => ({
      label: opcion.label,
      to: opcion.link,
      target: '_blank',
      onSelect: () => {
        open.value = false
      }
    }))
  }
])

// Scroll Store
const scroll = useScrollStore()
const { isScrolledInput } = storeToRefs(scroll)

// Home Store
const { isHome } = useIsHome()

// User data
const { user, clear } = useUserSession()
const byhoursConnect = useCookie('byhours_connect')
const menuItems = computed(() => [
  [
    {
      name: (user.value as OAuthUser)?.name,
      label: (user.value as OAuthUser)?.email,
      img: (user.value as OAuthUser)?.img,
      slot: 'account',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/bookings`)
      }
    }
  ],
  [
    {
      label: t('labels.user_zone.menu.reservas'),
      icon: 'i-lucide-building-2',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/bookings`)
      }
    },
    {
      label: t('labels.user_zone.menu.favoritos'),
      icon: 'i-heroicons-heart',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/favorites`)
      }
    },
    {
      label: t('labels.user_zone.menu.perfil'),
      icon: 'i-lucide-user-circle',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/profile`)
      }
    }
  ],
  [
    {
      label: t('labels.login.dropdown.cerrar'),
      icon: 'i-lucide-log-out',
      onClick: () => {
        clear()
        byhoursConnect.value = null
      }
    }
  ]
])

function generateUsername(email: string): string {
  if (!email?.includes("@")) {
    return "";
  }

  const [localPart] = email.split("@");
  return localPart?.replace(/[^a-zA-Z0-9]/g, "_") || "";
}

</script>

<template>
  <header class="top-0 left-0 right-0 w-full h-16 z-50 backdrop-blur-lg transition shadow-xs sticky bg-white/80">
    <div class="px-5 py-4 mx-auto flex justify-between items-center h-full"
      :class="`${isHome ? 'max-w-screen-2xl' : 'max-w-screen-2xl'}`">
      <div class="flex items-center w-full">

        <!-- Logo -->
        <NuxtLink :aria-label="$t('labels.aria.home')" :to="`/${locale}`">
          <Logo />
        </NuxtLink>

        <!-- Inputs -->
        <div class="w-full px-3 xl:flex" v-show="(isScrolledInput && isHome) || !isHome">
          <SearchInput />
        </div>
      </div>

      <div class="gap-3 items-center justify-end hidden xl:flex" :class="{ 'w-7/12': isScrolledInput && isHome }">
        <UNavigationMenu :key="locale" color="neutral" v-model="active" content-orientation="vertical" :items="items"
          class="w-full justify-center" :ui="{
            link: 'text-xs text-toned py-2',
            content: 'w-fit',
            linkLeadingIcon: 'size-4',

          }">
          <template #destinos-content="{ item }: { item: NavigationMenuItem }">
            <div class="p-5 w-92">
              <p class="text-sm font-bold mb-2 text-neutral-500 pl-2">{{
                t('strapi.global.header.labelDestinos').toUpperCase() }}
              </p>
              <ul class="grid lg:grid-cols-2">
                <li v-for="child in item.children" :key="child.label">
                  <UButton variant="link" :to="child.to"
                    class="text-sm text-left rounded-md p-2 px-4 transition-colors hover:bg-elevated/50 w-full"
                    @click="active = ''">
                    <p class="font-medium text-toned">
                      {{ child.label }}
                    </p>
                  </UButton>
                </li>
              </ul>
              <UButton block variant="outline" class="mt-5" color="neutral" :to="getButtonUrl(buttonDestinos)"
                target="_self" :aria-label="buttonDestinos.label" size="lg" :label="buttonDestinos.label"
                @click="active = ''" />
            </div>
          </template>

        </UNavigationMenu>

        <!-- Idioma y moneda -->
        <UPopover>
          <UButton size="xs" :aria-label="$t('labels.aria.idiomaMoneda')" variant="link" color="neutral"
            :label="`${selectedIdioma.toUpperCase()} | ${currency}`" />
          <template #content>
            <div class="flex flex-col gap-2 p-5 pt-3">
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloIdioma') }}</p>
                <LocaleSelector size="md" />
              </div>
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloMoneda') }}</p>
                <div>
                  <USelect :content="{ bodyLock: false }" class="w-full" size="md" v-model="currency"
                    :items="currencies" labelKey="id" value-key="id" />
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Sesión -->
        <AuthState v-slot="{ user, loggedIn }">
          <!-- Logged in -->
          <UDropdownMenu v-if="loggedIn" :items="menuItems">
            <UUser :name="(user as OAuthUser)?.name || generateUsername((user as OAuthUser)?.email)" :avatar="{
              src: (user as OAuthUser)?.img,
              alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
              icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
              ui: {
                root: 'bg-linear-to-r from-primary-500 to-pink-500 text-[8px]',
                fallback: 'text-white',
                icon: 'text-white text-sm',
              }
            }" class="p-1 pr-2 border border-neutral-300 rounded-full hover:cursor-pointer bg-gray-100 shadow-xs"
              size="2xs" />
            <template #account="{ item }">
              <UUser :name="item.name" :description="item.label" :avatar="{
                src: (user as OAuthUser)?.img,
                alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                size: 'md',
                ui: {
                  root: 'bg-linear-to-r from-primary-500 to-pink-500 text-xs',
                  fallback: 'text-white',
                  icon: 'text-white text-xl',
                }
              }" size="sm" class="text-left truncate" />
            </template>
          </UDropdownMenu>

          <!-- Not logged -->
          <UButton v-else dynamic :to="`/${locale}/login`" :aria-label="$t('labels.aria.usuario')" icon="i-lucide-user-circle"
            :label="$t('buttons.login')" size="xs" variant="subtle" color="neutral" />
        </AuthState>
      </div>

      <!-- Menu plegado -->
      <div class="xl:hidden">
        <UModal fullscreen :transition="false" v-model:open="open">
          <UButton :aria-label="$t('labels.aria.menu')" icon="i-lucide-menu" color="neutral" variant="link" />
          <template #title>
            <div class="px-1">
              <NuxtLink :aria-label="$t('labels.aria.home')" @click="open = false; navigateTo(`/${locale}`)">
                <Logo />
              </NuxtLink>
            </div>
          </template>
          <template #body>
            <div class="px-2">

              <UNavigationMenu :key="locale" color="neutral" orientation="vertical" content-orientation="vertical" :items="items"
                class="w-full justify-center" :ui="{
                  list: 'gap-2',
                  link: 'text-sm text-toned py-2',
                  content: 'w-fit'
                }">
                <template #destinos-content="{ item }: { item: NavigationMenuItem }">
                  {{ item }}
                </template>
              </UNavigationMenu>

              <USeparator class="py-4" />

              <!-- Idioma y Moneda -->
              <div class="flex flex-col gap-2 pb-4">

                <!-- Idioma -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloIdioma') }}</p>
                  <LocaleSelector class="w-full" size="xl" />
                </div>

                <!-- Moneda -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloMoneda') }}</p>
                  <div>
                    <USelect class="w-full" size="xl" v-model="currency" :items="currencies" labelKey="id"
                      value-key="id" />
                  </div>
                </div>
              </div>

              <USeparator class="py-2" />

              <!-- Sesión -->
              <AuthState v-slot="{ user, loggedIn, clear }">
                <div v-if="loggedIn" class="flex flex-col space-y-2 pt-1">
                  <div>
                    <UUser :name="(user as OAuthUser)?.name || generateUsername((user as OAuthUser)?.email)"
                      :description="(user as OAuthUser)?.email" :avatar="{
                        src: (user as OAuthUser)?.img,
                        alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                        icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                        size: 'sm',
                        ui: {
                          root: 'bg-linear-to-r from-primary-500 to-pink-500 text-xs',
                          fallback: 'text-white',
                          icon: 'text-white text-xl',
                        }
                      }" size="md" class="py-2 px-3 pr-4"
                      @click="navigateTo(`/${locale}/user-zone/bookings`); open = false" />
                  </div>
                  <UButton @click="clear(); open = false" :label="t('labels.login.dropdown.cerrar')"
                    icon="i-lucide-log-out" variant="ghost" color="neutral" />
                </div>
                <UButton v-else dynamic block :to="`/${locale}/login`" :aria-label="$t('labels.aria.usuario')" icon="i-lucide-user-circle"
                  :label="$t('buttons.login')" color="neutral" class="mt-2" size="lg" />
              </AuthState>
            </div>
          </template>
        </UModal>
      </div>
    </div>
  </header>
</template>